import React, { useState, useEffect } from 'react';
import {
  getProject,
  getProjectSummary,
  updateProject,
  uploadProjectLogo,
  getPMList,
  getProgressAlertsSummary,
  getDependencyAlerts,
  setProjectWorkWeek,
  setProjectTestDate,
} from '../api';
import { ProjectTodayContext, testDateOf, nowOn } from '../lib/projectToday';
import useConfirm from '../hooks/useConfirm';
import ConfirmDialog from '../components/ConfirmDialog';
import LoadingState from '../components/LoadingState';
import { daysUntil } from '../components/jobcost/JobCostShared';
import SOVTab from '../components/SOVTab';
import TasksTab from '../components/TasksTab';
import GanttTab from '../components/GanttTab';
import ScheduleView from '../components/ScheduleView';
import BillingTab from '../components/BillingTab';
import DocumentsTab from '../components/DocumentsTab';
import ForecastTab from '../components/ForecastTab';
import ChangeOrdersTab from '../components/ChangeOrdersTab';
import ContractValueTab from '../components/ContractValueTab';
import ClientViewTab from '../components/ClientViewTab';
import RollupTab from '../components/RollupTab';
import SchedulePerformanceTab from '../components/SchedulePerformanceTab';
import JobCostTab from '../components/JobCostTab';
import CloseoutTab from '../components/closeout/CloseoutTab';
import PunchListTab from '../components/subs/PunchListTab';
import ProjectSubcontractorsTab from '../components/subs/ProjectSubcontractorsTab';
import { useTheme } from '../hooks/useTheme';
import ProjectHeader from '../components/project/ProjectHeader';
import TabBar from '../components/project/TabBar';
import KpiStrip from '../components/project/KpiStrip';
import useContractBreakdown from '../hooks/useContractBreakdown';
import ErrorBoundary from '../components/ErrorBoundary';
import ProjectOverview from '../components/project/overview/ProjectOverview';

const TABS = [
  { key: 'dashboard', label: 'Overview' },
  { key: 'sov', label: 'SOV' },
  { key: 'tasks', label: 'Schedule' },
  { key: 'subcontractors', label: 'Subcontractors' },
  { key: 'rollup', label: 'Rollup' },
  { key: 'billing', label: 'Billing' },
  { key: 'performance', label: 'Performance' },
  { key: 'forecast', label: 'Forecast' },
  { key: 'contract value', label: 'Contract Value' },
  { key: 'job-costs', label: 'Job Costs' },
  { key: 'closeout', label: 'Closeout' },
  { key: 'punch', label: 'Punch list' }, // the subs' punch items, next to Closeout (Duncan 2026-10-26)
  { key: 'documents', label: 'Documents' },
  { key: 'client view', label: 'Client View' },
];

const ProjectDetail = ({ projectId, onBack, activeTab: externalTab, setActiveTab: externalSetActiveTab, user, darkMode, onToggleDarkMode, onLogout }) => {
  const [internalTab, setInternalTab] = useState('dashboard');
  const activeTab = externalTab || internalTab;
  const setActiveTab = externalSetActiveTab || setInternalTab;
  // Performance sub-view (Schedule / Billing) state lives inside the
  // redesigned SchedulePerformanceTab component now — no longer needs
  // to be tracked here.
  const [project, setProject] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editedProject, setEditedProject] = useState(null);
  // ScheduleView now owns its own task-edit modal; we just keep a refresh key
  // for callers (if any) that want to force a re-fetch by bumping it.
  const [ganttRefreshKey] = useState(0);
  const [pmList, setPMList] = useState([]);
  const [logoUploading, setLogoUploading] = useState(false);
  const [editingCollected, setEditingCollected] = useState(false);
  const [collectedInput, setCollectedInput] = useState('');
  const [alertsSummary, setAlertsSummary] = useState(null);
  const [depAlerts, setDepAlerts] = useState(null);
  const t = useTheme();
  // What makes up the contract on file -- the top bar's Contract value drops it down (Duncan 2026-11-03). Refetched with
  // the summary, so it follows an SOV or contract change.
  const contractBreakdown = useContractBreakdown(project && project.id, summary);

  useEffect(() => {
    loadProjectData();
    getPMList().then(setPMList).catch(console.error);
    getProgressAlertsSummary(projectId).then(setAlertsSummary).catch(console.error);
    getDependencyAlerts(projectId).then(setDepAlerts).catch(console.error);
  }, [projectId]);

  const loadProjectData = async () => {
    setLoading(true);
    try {
      const [projectData, summaryData] = await Promise.all([
        getProject(projectId),
        getProjectSummary(projectId),
      ]);
      setProject(projectData);
      setSummary(summaryData);
      setEditedProject(projectData);
    } catch (error) {
      console.error('Error loading project data:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshSummary = async () => {
    try {
      const summaryData = await getProjectSummary(projectId);
      setSummary(summaryData);
    } catch (error) {
      console.error('Error refreshing summary:', error);
    }
  };

  /* Save as you go (Duncan 2026-10-26, the Overview redesign): a field saves when you leave it, just that field -- the
     whole project went before, which is how an empty Contract for became the word "null". A failed save puts the field
     back and says why. */
  const [saveState, setSaveState] = useState(null); // { status: 'saving' | 'saved' | 'error', text }
  const SUMMARY_FIELDS = ['start_date', 'end_date', 'client_name', 'project_manager', 'construction_manager', 'salesperson', 'project_developer'];
  const saveField = async (field, value) => {
    const before = project ? project[field] : undefined;
    setEditedProject((prev) => ({ ...prev, [field]: value }));
    setSaveState({ status: 'saving' });
    try {
      const updated = await updateProject(projectId, { [field]: value });
      setProject((prev) => ({ ...prev, ...(updated || { [field]: value }) }));
      setEditedProject((prev) => ({ ...prev, [field]: updated ? updated[field] : value }));
      setSaveState({ status: 'saved', at: Date.now() });
      if (SUMMARY_FIELDS.includes(field)) refreshSummary();
    } catch (error) {
      setEditedProject((prev) => ({ ...prev, [field]: before }));
      setSaveState({ status: 'error', text: error.code === 'DUPLICATE_PROJECT_NUMBER' ? error.message : `Not saved: ${error.message || 'try again'}` });
    }
  };

  const handleCollectedSave = async () => {
    const val = parseFloat(collectedInput);
    if (isNaN(val) || val < 0) return;
    try {
      await updateProject(projectId, { collected_to_date: val });
      await loadProjectData();
      setEditingCollected(false);
    } catch (err) { console.error('Failed to update collected:', err); }
  };

  const handleCurrencyEditChange = (field, value) => {
    const raw = value.replace(/[^0-9.]/g, '');
    const parts = raw.split('.');
    const cleaned = parts[0] + (parts.length > 1 ? '.' + parts.slice(1).join('') : '');
    setEditedProject({ ...editedProject, [field]: cleaned });
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      alert('Image must be under 2MB.');
      return;
    }
    setLogoUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        // Resize image to max 200x200 for storage efficiency
        const img = new Image();
        img.onload = async () => {
          const canvas = document.createElement('canvas');
          const max = 200;
          let w = img.width, h = img.height;
          if (w > max || h > max) {
            if (w > h) { h = Math.round(h * max / w); w = max; }
            else { w = Math.round(w * max / h); h = max; }
          }
          canvas.width = w;
          canvas.height = h;
          canvas.getContext('2d').drawImage(img, 0, 0, w, h);
          const dataUrl = canvas.toDataURL('image/png');
          await uploadProjectLogo(projectId, dataUrl);
          setProject(prev => ({ ...prev, logo_url: dataUrl }));
          setEditedProject(prev => ({ ...prev, logo_url: dataUrl }));
          setLogoUploading(false);
        };
        img.src = reader.result;
      } catch (err) {
        console.error('Logo upload failed:', err);
        alert('Failed to upload logo.');
        setLogoUploading(false);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleRemoveLogo = async () => {
    try {
      setLogoUploading(true);
      await uploadProjectLogo(projectId, null);
      setProject(prev => ({ ...prev, logo_url: null }));
      setEditedProject(prev => ({ ...prev, logo_url: null }));
    } catch (err) {
      console.error('Failed to remove logo:', err);
    } finally {
      setLogoUploading(false);
    }
  };

  const formatCurrencyInput = (value) => {
    if (!value || value === '') return '';
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    const parts = String(value).split('.');
    const intPart = parseInt(parts[0] || '0', 10).toLocaleString('en-US');
    if (parts.length > 1) {
      return intPart + '.' + parts[1];
    }
    return intPart;
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '$0.00';
    return `$${Number(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    // A date-only 'YYYY-MM-DD' (start/end, the schedule's last day) is a calendar day:
    // read it as local midnight. new Date() takes it as UTC midnight — a day early here.
    const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateString));
    const date = ymd ? new Date(+ymd[1], +ymd[2] - 1, +ymd[3]) : new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  };

  if (loading) {
    return <LoadingState message="Loading project data…" />;
  }

  if (!project || !summary) {
    return (
      <div style={{ padding: '20px' }}>
        <button className="btn btn-secondary" onClick={onBack}>
          &larr; Back to Projects
        </button>
        <p style={{ color: '#f44336', marginTop: '16px' }}>Error loading project data</p>
      </div>
    );
  }

  const contractVal = summary.contract_value || 0;
  const allocatedVal = summary.allocated_value || 0;
  // Summit pay apps or CE's invoices, whichever is further along (#63; server/ceBilling.js).
  // A summary without `billing` (an older server mid-deploy) gets today's Summit figures.
  const manualCollected = parseFloat(project?.collected_to_date) || 0;
  const billing = summary.billing || {
    source: 'summit',
    billed: summary.billed_to_date || 0,
    retainage: summary.retainage_held || 0,
    remaining: summary.remaining || 0,
    collected: manualCollected,
    outstanding: Math.max(0, (summary.billed_to_date || 0) - manualCollected),
  };
  const billedVal = billing.billed;
  const retainageVal = billing.retainage;
  const remainingVal = billing.remaining;
  const billedPercent = contractVal > 0 ? ((billedVal / contractVal) * 100).toFixed(1) : 0;

  // Days remaining in project
  const projectEndDate = summary.latest_task_finish || project.end_date;
  // Calendar days, counted the same way as the Job Costs tab's due line.
  // A test job's test date stands in for today (Duncan 2026-10-20).
  const projectNow = nowOn(testDateOf(project));
  const endIn = projectEndDate ? daysUntil(projectEndDate, projectNow) : null;
  const daysRemaining = projectEndDate
    ? Math.max(0, endIn ?? Math.ceil((new Date(projectEndDate) - projectNow) / (1000 * 60 * 60 * 24)))
    : null;

  // Every tab reads a test job's test date from here; the Gantt's Test date control saves it.
  const todayContext = {
    testDate: testDateOf(project),
    isTest: !!project.is_test,
    setTestDate: async (ymd) => {
      const saved = await setProjectTestDate(project.id, ymd);
      setProject((prev) => (prev ? { ...prev, test_today: saved.test_today } : prev));
      refreshSummary();
    },
  };

  return (
    <ProjectTodayContext.Provider value={todayContext}>
    {/* clip, not hidden: hidden would make this a scroll container that never scrolls,
        and the pinned header band below would stick to it instead of the window. */}
    <div style={{ maxWidth: '100%', overflowX: 'clip' }}>
      {/* Redesigned chrome — sits inside .app-main's 24px gutter so
          the outer corners on ProjectHeader (top) and KpiStrip
          (bottom) can show their 12px rounding against the
          --bg-page background. Top padding on .app-main was zeroed
          for project-detail routes (so sticky positioning anchors at
          y=0 without offsetting the TabBar), but a small top margin
          here gives the rounded top a comfortable breathing point.
          ProjectHeader + TabBar stay pinned to the top of the window
          as one band; its --bg-page fill keeps their rounded corners
          clean while the page scrolls under them. The KpiStrip
          scrolls away. */}
      <div data-project-head style={{ position: 'sticky', top: 0, zIndex: 100, padding: '8px 0 0', background: 'var(--bg-page)' }}>
        <ProjectHeader
          project={project}
          summary={summary}
          user={user}
          onBack={onBack}
          formatDate={formatDate}
          darkMode={darkMode}
          onToggleDarkMode={onToggleDarkMode}
          onLogout={onLogout}
        />
        <TabBar
          tabs={TABS.filter((t) => t.key !== 'client view')}
          activeTab={activeTab}
          onChange={setActiveTab}
          // Hidden for now (Duncan 2026-10-21: "get rid of this for now"); the tab itself still exists.
          showClientView={false}
        />
      </div>
      {/* 8px gutter of --bg-page so the TabBar and KpiStrip read as
          two distinct bands instead of one fused chrome region.
          Inner wrapper also adds the strip's top border so the two
          bands hand off cleanly. */}
      <div style={{ background: 'var(--bg-page)', paddingTop: 8 }}>
        <KpiStrip
          contractBreakdown={contractBreakdown}
          onOpenSov={() => setActiveTab('sov')}
          contractValue={contractVal}
          sovAllocated={allocatedVal}
          billedToDate={billedVal}
          remaining={remainingVal}
          daysRemaining={daysRemaining}
          collectedToDate={billing.collected}
          outstanding={billing.outstanding}
          retainageHeld={retainageVal}
          billedPercent={parseFloat(billedPercent) || 0}
          // When CE's figures show, Collected is CE's collections: the hand-entered
          // figure would change nothing on screen, so it isn't offered.
          onEditCollected={billing.source === 'ce' ? undefined : () => {
            if (!editingCollected) {
              setCollectedInput(manualCollected.toFixed(2));
              setEditingCollected(true);
            }
          }}
        />
      </div>

      {/* Edit Collected popover — small floating dialog above the page so the
          tight KPI cell doesn't need to host an inline input. */}
      {editingCollected && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 200,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => setEditingCollected(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-surface)', border: '1px solid var(--border-default)',
              borderRadius: 10, padding: 16, minWidth: 280, boxShadow: 'var(--shadow-md)',
              display: 'flex', flexDirection: 'column', gap: 10,
            }}
          >
            <div style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--text-1)' }}>Update Collected to Date</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 16.5, color: 'var(--text-2)' }}>$</span>
              <input
                type="number" step="0.01" min="0"
                value={collectedInput}
                onChange={(e) => setCollectedInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCollectedSave(); if (e.key === 'Escape') setEditingCollected(false); }}
                autoFocus
                style={{
                  flex: 1, padding: '6px 8px', fontSize: 15.5,
                  border: '1px solid var(--border-default)', borderRadius: 6,
                  background: 'var(--bg-surface)', color: 'var(--text-1)', outline: 'none',
                  fontFamily: 'var(--font-mono)',
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setEditingCollected(false)}
                style={{
                  padding: '6px 12px', fontSize: 14, fontWeight: 500, borderRadius: 6,
                  border: '1px solid var(--border-default)', background: 'var(--bg-surface)',
                  color: 'var(--text-2)', cursor: 'pointer',
                }}
              >Cancel</button>
              <button
                type="button"
                onClick={handleCollectedSave}
                style={{
                  padding: '6px 12px', fontSize: 14, fontWeight: 500, borderRadius: 6,
                  border: 'none', background: 'var(--accent)', color: 'white', cursor: 'pointer',
                }}
              >Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Tab Content — wrapped in an in-chrome ErrorBoundary so a
          render failure inside one tab shows a polite state screen
          without nuking the ProjectHeader + TabBar + KpiStrip above. */}
      <ErrorBoundary variant="in-chrome">
      {/* clip on the Schedule tab (2026-10-24): hidden makes this a scroll container that never scrolls, so the
          Gantt's sticky scrollbar stuck to it -- under the last task -- instead of the bottom of the window. The same
          on the Punch list (2026-10-26) for its page rail, which stays in view as the page scrolls. */}
      <div style={{ marginTop: '20px', maxWidth: '100%', overflowX: activeTab === 'tasks' || activeTab === 'gantt' || activeTab === 'punch' ? 'clip' : 'hidden' }}>

        {activeTab === 'dashboard' && (
          <ProjectOverview
            projectId={projectId}
            edited={editedProject}
            summary={summary}
            billing={billing}
            contractVal={contractVal}
            projectNow={projectNow}
            isTest={!!project.is_test}
            pmList={pmList}
            alertsSummary={alertsSummary}
            depAlerts={depAlerts}
            logoUploading={logoUploading}
            onLogoUpload={(file) => handleLogoUpload({ target: { files: [file], value: '' } })}
            onRemoveLogo={handleRemoveLogo}
            saveField={saveField}
            saveState={saveState}
            onOpenTab={setActiveTab}
            workWeek={project?.work_week}
            // In place, not loadProjectData: a reload unmounts the Overview.
            onWorkWeekChanged={(week) => { setProject((prev) => (prev ? { ...prev, work_week: week } : prev)); refreshSummary(); }}
          />
        )}

        {activeTab === 'sov' && (
          <SOVTab projectId={projectId} refreshSummary={refreshSummary} />
        )}
        {(activeTab === 'tasks' || activeTab === 'gantt') && (
          <ScheduleView
            projectId={projectId}
            refreshSummary={refreshSummary}
            refreshKey={ganttRefreshKey}
            workWeek={project?.work_week || '5'}
          />
        )}
        {activeTab === 'subcontractors' && (
          <ProjectSubcontractorsTab projectId={projectId} user={user} />
        )}
        {activeTab === 'rollup' && (
          <RollupTab projectId={projectId} refreshSummary={refreshSummary} />
        )}
        {activeTab === 'billing' && (
          <BillingTab projectId={projectId} refreshSummary={refreshSummary} />
        )}
        {activeTab === 'documents' && (
          <DocumentsTab projectId={projectId} user={user} />
        )}
        {activeTab === 'performance' && (
          <SchedulePerformanceTab projectId={projectId} contractValue={contractVal} />
        )}
        {activeTab === 'forecast' && (
          <ForecastTab projectId={projectId} refreshSummary={refreshSummary} />
        )}
        {activeTab === 'contract value' && (
          <ContractValueTab projectId={projectId} refreshSummary={refreshSummary} />
        )}
        {activeTab === 'job-costs' && (
          <JobCostTab projectId={projectId} project={project} endDate={projectEndDate || null} onOpenSov={() => setActiveTab('sov')} />
        )}
        {activeTab === 'closeout' && (
          <CloseoutTab projectId={projectId} project={project} />
        )}
        {activeTab === 'punch' && (
          <PunchListTab projectId={projectId} />
        )}
        {activeTab === 'client view' && (
          <ClientViewTab projectId={projectId} refreshSummary={refreshSummary} workWeek={project?.work_week || '5'} />
        )}
      </div>
      </ErrorBoundary>

    </div>
    </ProjectTodayContext.Provider>
  );
};

export default ProjectDetail;
